"use client"

import Link from "next/link"
import Image from "next/image"
import { Star, MapPin, Clock, DollarSign, BadgeCheck } from "lucide-react"
import type { RankedUser } from "@/lib/ranking"

type Props = {
  user: RankedUser
  rank: number
}

function StarRating({ rating, count, source }: { rating: number; count: number; source?: "google" | "bluecollar" }) {
  const full = Math.floor(rating)
  const half = rating - full >= 0.5

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`w-3.5 h-3.5 ${
              i <= full
                ? "fill-amber-400 text-amber-400"
                : half && i === full + 1
                ? "fill-amber-200 text-amber-400"
                : "text-gray-300"
            }`}
          />
        ))}
      </div>
      <span className="text-sm font-medium text-gray-700">{rating.toFixed(1)}</span>
      <span className="text-sm text-gray-400">
        ({count} review{count !== 1 ? "s" : ""}{source === "google" ? " on Google" : ""})
      </span>
    </div>
  )
}

export default function DirectoryListingCard({ user, rank }: Props) {
  const initials = (user.name ?? "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  const isTopRanked = rank <= 3
  const isUnclaimed = !!(user as RankedUser & { isPlacesListing?: boolean; claimToken?: string | null }).isPlacesListing &&
    !!(user as RankedUser & { claimToken?: string | null }).claimToken

  const placesRating = (user as RankedUser & { placesRating?: number | null }).placesRating
  const placesReviewCount = (user as RankedUser & { placesReviewCount?: number | null }).placesReviewCount
  const claimToken = (user as RankedUser & { claimToken?: string | null }).claimToken

  return (
    <Link href={`/profile/${user.id}`} className="block group">
      <div
        className={`bg-white rounded-2xl border transition-all hover:shadow-md ${
          isTopRanked && !isUnclaimed ? "border-amber-200" : "border-gray-200"
        } ${isUnclaimed ? "opacity-90" : ""}`}
      >
        {/* Top-ranked banner (only for real claimed users) */}
        {rank === 1 && !isUnclaimed && (
          <div className="bg-amber-500 text-white text-xs font-semibold px-4 py-1 rounded-t-2xl">
            ★ Top Ranked in this area
          </div>
        )}

        {/* Unclaimed banner — links to claim page independently from the card */}
        {isUnclaimed && claimToken && (
          <a
            href={`/directory/claim/${claimToken}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-between gap-2 bg-gray-100 hover:bg-amber-50 px-4 py-1.5 rounded-t-2xl transition-colors"
          >
            <span className="text-xs text-gray-500">
              Unclaimed listing · Is this your business?
            </span>
            <span className="text-xs text-amber-600 font-semibold whitespace-nowrap">
              Claim it today →
            </span>
          </a>
        )}

        <div className="p-5 flex gap-4">
          {/* Rank badge + avatar */}
          <div className="flex flex-col items-center gap-2 flex-shrink-0">
            <span className="text-xs font-bold text-gray-400 w-6 text-center">#{rank}</span>
            {user.profileImage ? (
              <Image
                src={user.profileImage}
                alt={user.name ?? ""}
                width={56}
                height={56}
                className="w-14 h-14 rounded-full object-cover"
              />
            ) : (
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center font-bold text-lg ${
                  isUnclaimed
                    ? "bg-gray-100 text-gray-400"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                {initials}
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-amber-700 transition-colors">
                    {user.name}
                  </h3>
                  {/* Verified badge — real users with a website */}
                  {user.website && !isUnclaimed && (
                    <BadgeCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-gray-500">{user.headline ?? user.trade}</p>
              </div>

              {/* Hourly rate (only for claimed profiles) */}
              {user.hourlyRate && !isUnclaimed && (
                <div className="flex items-center gap-0.5 text-sm font-medium text-gray-700 flex-shrink-0">
                  <DollarSign className="w-3.5 h-3.5" />
                  {user.hourlyRate}/hr
                </div>
              )}
            </div>

            {/* Rating */}
            <div className="mt-2">
              {/* Real BlueCollarSocial reviews */}
              {user.reviewCount > 0 && (
                <StarRating rating={user.avgRating} count={user.reviewCount} />
              )}
              {/* Google rating for unclaimed listings */}
              {user.reviewCount === 0 && isUnclaimed && placesRating && placesReviewCount && placesReviewCount > 0 && (
                <StarRating rating={placesRating} count={placesReviewCount} source="google" />
              )}
              {/* No rating at all */}
              {user.reviewCount === 0 && (!isUnclaimed || !placesRating) && (
                <span className="text-xs text-gray-400 italic">No reviews yet</span>
              )}
            </div>

            {/* Bio excerpt (hide auto-generated bio for unclaimed — it's redundant) */}
            {user.bio && !isUnclaimed && (
              <p className="mt-2 text-sm text-gray-600 line-clamp-2">{user.bio}</p>
            )}

            {/* Footer meta */}
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500">
              {user.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {user.location}
                </span>
              )}
              {user.yearsOfExperience && !isUnclaimed && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {user.yearsOfExperience} yr{user.yearsOfExperience !== 1 ? "s" : ""} exp
                </span>
              )}
              {!isUnclaimed && user.skills.slice(0, 3).map((s) => (
                <span
                  key={s}
                  className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}
